import { Box, Typography, alpha } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { useTranslation } from 'react-i18next';
import theme from '../../../theme/theme';
import { DetailCard } from '../../../components/ui/DetailCard';
import { FieldGrid, type VisibleField } from '../../../components/ui/FieldGrid';
import { Badge } from '../../../components/ui/Badge';
import { BADGE_VARIANTS } from '../../../components/ui/badgeVariants';
import { formatDate, dateFormatForLang } from '../../../lib/date/formatDate';
import { PAYSLIP_CLOSURE_KEY, type PayslipDetailHeader } from '../payslipTypes';
import { PAYSLIP_CLOSURE_VARIANT } from '../config/payslipPresentation';
import { formatPayslipPeriod } from '../config/payslipFormat';

const I18N = 'payments:payslips.detail';

/**
 * Frozen payslip header — identity, function/level, analytical project/axis,
 * contract dates and seniority, as copied on the `bulletin` at pay time (never the
 * employee's current record). Reuses {@link DetailCard} + {@link FieldGrid}; the
 * closure status is a {@link Badge} in the header, and a "frozen at pay time" lock
 * badge signals the read-only, point-in-time nature.
 */
export function PayslipFrozenHeader({ header }: { header: PayslipDetailHeader }) {
  const { t, i18n } = useTranslation('payments');
  const dateFormat = dateFormatForLang(i18n.language);
  const displayDate = (iso: string | null): string | null =>
    iso ? formatDate(iso, dateFormat) || null : null;

  // Analytique : "libellé · code" quand le libellé est résolu, sinon le code brut.
  const withCode = (libelle: string | null, code: string | null): string | null =>
    libelle ? (code ? `${libelle} · ${code}` : libelle) : code || null;

  // Reading order (3-column grid), grouped by row:
  //   N° dossier / Nom / Prénom
  //   Date d'entrée / Date début contrat / Ancienneté
  //   Période de paie / Fonction / Niveau
  //   Code projet / Sous-projet / —(spacer)
  const fields: VisibleField[] = [
    { id: 'code_staff', label: t(`${I18N}.header.fileNumber`), value: header.code_staff },
    { id: 'nom', label: t(`${I18N}.header.lastName`), value: header.nom },
    { id: 'prenom', label: t(`${I18N}.header.firstName`), value: header.prenom },
    { id: 'entryDate', label: t(`${I18N}.header.entryDate`), value: displayDate(header.dateentree) },
    {
      id: 'contractStartDate',
      label: t(`${I18N}.header.contractStartDate`),
      value: displayDate(header.datedeb),
    },
    {
      id: 'seniority',
      label: t(`${I18N}.header.seniority`),
      value: header.anciennete != null ? String(header.anciennete) : null,
    },
    {
      id: 'period',
      // Localized "short month + year" (e.g. 202608 → "Aug 2026" / "août 2026").
      label: t(`${I18N}.header.period`),
      value: header.mois ? formatPayslipPeriod(header.mois, i18n.language) : null,
    },
    { id: 'function', label: t(`${I18N}.header.function`), value: header.libfonction },
    { id: 'level', label: t(`${I18N}.header.level`), value: header.niveau },
    {
      id: 'projectCode',
      label: t(`${I18N}.header.projectCode`),
      value: withCode(header.lib_projet, header.codeanalytique),
    },
    {
      id: 'subProject',
      label: t(`${I18N}.header.subProject`),
      value: withCode(header.lib_axeanalytique, header.axeanalytique),
    },
    // Keep "Code projet / Sous-projet" together; trailing spacer fills the row.
    { id: 'spacer', label: '', value: null, spacer: true },
  ];

  const closure = PAYSLIP_CLOSURE_VARIANT[PAYSLIP_CLOSURE_KEY[header.statut]];
  const closureColors = BADGE_VARIANTS[closure];

  return (
    <DetailCard
      icon={
        <PersonOutlineIcon
          sx={{ fontSize: theme.iconSize.sm, color: theme.palette.primary.main }}
        />
      }
      iconBg={alpha(theme.palette.primary.main, 0.12)}
      title={t(`${I18N}.header.title`)}
      headerExtra={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Badge
            label={t(`payments:payslips.closureStatus.${PAYSLIP_CLOSURE_KEY[header.statut]}`)}
            color={closureColors.color}
            bgcolor={closureColors.bgcolor}
          />
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              color: theme.palette.text.label,
            }}
          >
            <LockOutlinedIcon sx={{ fontSize: theme.iconSize.xs }} />
            <Typography variant="caption">{t(`${I18N}.header.frozen`)}</Typography>
          </Box>
        </Box>
      }
    >
      <FieldGrid fields={fields} columns={3} />
    </DetailCard>
  );
}
